import { useState, useCallback } from "react";
import { roundRate } from "@/lib/utils";
import { Step } from "./constants";
import { CaretLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { DrawerHeader } from "@/components/ui/drawer";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Currency } from "@/lib/types";

// Format rate value for display (up to 6 decimal places, no trailing zeros)
const formatRateValue = (rate: number): string => {
  if (!rate || !isFinite(rate)) return "";
  return parseFloat(rate.toFixed(6)).toString();
};

const ExchangeRateStep = ({
  setStep,
  transactionCurrency,
  accountCurrency,
  initialRate,
  onSave,
}: {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  transactionCurrency?: Currency;
  accountCurrency?: Currency;
  /** Current rate from useConversionRate — seeds the drawer's local input state. */
  initialRate: number | undefined;
  /** Called with the saved rate; routes through useConversionRate's setRate so the displayed value updates. */
  onSave: (rate: number | undefined) => void;
}) => {
  const { t } = useTranslation();

  // Dual-direction state:
  // fromToRate: 1 Transaction Currency = X Account Currency (e.g., 1 TRY = 0.026 USD)
  // toFromRate: 1 Account Currency = X Transaction Currency (e.g., 1 USD = 38 TRY)
  const [fromToRate, setFromToRate] = useState<string>(() =>
    initialRate !== undefined && initialRate > 0
      ? formatRateValue(initialRate)
      : ""
  );
  const [toFromRate, setToFromRate] = useState<string>(() =>
    initialRate !== undefined && initialRate > 0
      ? formatRateValue(1 / initialRate)
      : ""
  );

  const transactionCode = transactionCurrency?.code || "???";
  const accountCode = accountCurrency?.code || "???";

  // Handle "1 Transaction = X Account" input change (fromTo direction)
  const handleFromToChange = useCallback((value: string) => {
    setFromToRate(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      const inverse = 1 / numValue;
      setToFromRate(formatRateValue(inverse));
    } else if (value === "") {
      setToFromRate("");
    }
  }, []);

  // Handle "1 Account = X Transaction" input change (toFrom direction)
  const handleToFromChange = useCallback((value: string) => {
    setToFromRate(value);

    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      const inverse = 1 / numValue;
      setFromToRate(formatRateValue(inverse));
    } else if (value === "") {
      setFromToRate("");
    }
  }, []);

  const handleSave = () => {
    const numValue = parseFloat(fromToRate);
    if (!isNaN(numValue) && numValue > 0) {
      onSave(roundRate(numValue));
    }
    setStep(Step.INITAL);
  };

  return (
    <div>
      <DrawerHeader
        title={t("label.add_exchange_rate")}
        leftButton={
          <button type="button" onClick={() => setStep(Step.INITAL)}>
            <CaretLeft color="#E3EDFC" size={18} />
          </button>
        }
        rightButton={<span className="w-[18px]" />}
      />

      <p className="mx-auto mb-4 max-w-[280px] text-center text-sm font-medium leading-[18px] text-[#98B2D3]">
        {t("description.exchange_rate_hint")}
      </p>

      {/* 1 Account Currency = X Transaction Currency (more intuitive for users) */}
      <div className="mb-3 flex items-center gap-2">
        <span className="min-w-[70px] text-sm text-white/70">
          1 {accountCode} =
        </span>
        <Input
          type="number"
          data-testid="expense-form-exchange-rate-to-from-input"
          placeholder="0.00"
          value={toFromRate}
          onChange={(e) => handleToFromChange(e.target.value)}
          className="flex-1"
        />
        <span className="min-w-[40px] text-sm text-white/70">
          {transactionCode}
        </span>
      </div>

      {/* 1 Transaction Currency = X Account Currency */}
      <div className="mb-4 flex items-center gap-2">
        <span className="min-w-[70px] text-sm text-white/70">
          1 {transactionCode} =
        </span>
        <Input
          type="number"
          data-testid="expense-form-exchange-rate-from-to-input"
          placeholder="0.00"
          value={fromToRate}
          onChange={(e) => handleFromToChange(e.target.value)}
          className="flex-1"
        />
        <span className="min-w-[40px] text-sm text-white/70">
          {accountCode}
        </span>
      </div>

      <Button
        data-testid="expense-form-exchange-rate-submit-button"
        className="mt-4 w-full"
        onClick={handleSave}
      >
        {t("button.add")}
      </Button>
    </div>
  );
};

export default ExchangeRateStep;
